import { Screen } from '../../app/Frame';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatDayYear } from '../../lib/time';
import { formatKop } from '../../lib/money';
import { Notice, Pill } from '../../ui/parts';
import { FormSkeleton, LoadFailed } from '../org-parts';
import { useAdminSettings, useSubscription, useSubscriptionMutations } from '../api';
import { CancelSubscriptionSheet, ChangePlanSheet, ChoosePlanSheet } from '../SubscriptionSheets';
import type { PlanInfo, Subscription } from '../types';

/** Подпись состояния подписки: тариф, если он оплачен, иначе — сам режим. */
export function planLabel(
  status: Subscription['status'], plan: string, plans: Record<string, PlanInfo>,
): string {
  if (status === 'trial') return 'Демо-период';
  if (status === 'active') return plans[plan]?.label ?? 'Подписка активна';
  if (status === 'readonly' || status === 'cancelled') return 'Только просмотр';
  return 'Просрочена';
}

export function subscriptionPill(status: Subscription['status']): 'paid' | 'await' | 'off' | 'done' {
  if (status === 'active') return 'paid';
  if (status === 'trial') return 'await';
  return 'off';
}

/**
 * Раздел «Подписка».
 *
 * Дату и сумму следующего списания здесь не показать, и не потому, что их не
 * нашли: автопродления в сервисе нет — оплата разовая (`billing.ts`, ЮKassa
 * зовётся только по кнопке). Поэтому и действие называется «Отменить подписку»,
 * а не «Отключить автопродление»: оно не выключает продление, а закрывает
 * доступ по окончании оплаченного периода.
 */
export function OrgSubscription() {
  const sheet = useSheet();
  const toast = useToast();
  const settings = useAdminSettings();
  const sub = useSubscription();
  const { resume } = useSubscriptionMutations();

  useScreen('Подписка', null);

  if (sub.isError) {
    return <Screen><LoadFailed busy={sub.isFetching} onRetry={() => void sub.refetch()} /></Screen>;
  }
  if (!sub.data) {
    return (
      <Screen>
        <FormSkeleton rows={3} />
      </Screen>
    );
  }

  const tz = settings.data?.settings.tz ?? 'Europe/Moscow';
  const subscription = sub.data.subscription;
  const plans = sub.data.plans;

  if (!subscription) {
    return (
      <Screen>
        <p className="lead">У организации нет подписки — её заводит владелец сервиса.</p>
      </Screen>
    );
  }

  const until = subscription.status === 'trial'
    ? subscription.trial_ends_at
    : subscription.current_period_end;
  /** Оплаченная подписка: только её можно отменить или сменить. */
  const paidNow = subscription.status === 'active';
  const plan = plans[subscription.plan];
  const cancelled = !!subscription.cancel_at_period_end;

  return (
    <Screen>
      <p className="lead">Чем организация пользуется сейчас и до какого числа</p>

      <p className="sub-status">
        <Pill kind={subscriptionPill(subscription.status)}>
          {planLabel(subscription.status, subscription.plan, plans)}
        </Pill>
        {until ? <span className="when">до {formatDayYear(until, tz)}</span> : null}
      </p>

      {/* Подробности — только у оплаченной подписки и только те, что есть в
          данных. У демо-периода их нет: тариф не выбран, платежа не было, и
          строка «Тариф: 1 месяц» обещала бы то, чего организация не покупала. */}
      {paidNow && plan ? (
        <div className="kv kv-wrap">
          <div className="kv-row">
            <span className="k">Тариф</span>
            <span className="v">{plan.label}</span>
          </div>
          {until ? (
            <div className="kv-row">
              <span className="k">Действует до</span>
              <span className="v">{formatDayYear(until, tz)}</span>
            </div>
          ) : null}
          <div className="kv-row">
            <span className="k">Оплачено за период</span>
            <span className="v">{formatKop(plan.priceKop)}</span>
          </div>
        </div>
      ) : null}

      {cancelled ? (
        <Notice>
          Подписка отменена и не продлится: после этой даты организация перейдёт в режим только
          для просмотра. Передумали — нажмите «Возобновить подписку», платить повторно не нужно.
        </Notice>
      ) : null}

      {subscription.status === 'readonly' || subscription.status === 'cancelled' ? (
        <Notice>
          Только просмотр: существующие записи и напоминания сохранятся, но новые создать нельзя,
          пока подписка не оплачена.
        </Notice>
      ) : null}

      <div className="col-actions">
        {paidNow ? (
          cancelled ? (
            <button
              className="btn solid"
              disabled={resume.isPending}
              onClick={() => resume.mutate(undefined, {
                onSuccess: () => toast('Подписка возобновлена'),
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось возобновить'),
              })}
            >
              Возобновить подписку
            </button>
          ) : (
            <button
              className="btn solid"
              disabled={sub.isPending}
              onClick={() => sheet.open(<ChangePlanSheet quotes={sub.data?.quotes ?? []} current={subscription.plan} />)}
            >
              Изменить тариф
            </button>
          )
        ) : (
          <button className="btn solid" onClick={() => sheet.open(<ChoosePlanSheet plans={plans} />)}>
            Выбрать тариф
          </button>
        )}
      </div>

      <p className="hint">
        Автопродления нет: оплата разовая, и по окончании периода вы сами решаете, продлевать
        подписку или нет.
      </p>

      {/* Отмена — вторичное действие: рядом с «Изменить тариф» равновеликой
          кнопкой она читалась как такой же обычный шаг. Подтверждение и то, что
          доступ живёт до конца оплаченного периода, остались в шторке. */}
      {paidNow && !cancelled ? (
        <div className="danger-zone">
          <button
            className="linkish danger"
            type="button"
            onClick={() => sheet.open(<CancelSubscriptionSheet subscription={subscription} />)}
          >
            Отменить подписку
          </button>
          <span className="hint">Доступ сохранится до конца оплаченного периода</span>
        </div>
      ) : null}
    </Screen>
  );
}
